import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentPropsWithoutRef } from "react";

import { cn } from "core/shadcn/lib/utils";

const textVariants = cva("", {
  variants: {
    tone: {
      default: "",
      muted: "text-muted-foreground",
      danger: "text-sb-red-500",
      success: "text-sb-success-500",
      warning: "text-amber-500",
    },
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
    },
    weight: {
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
    },
  },
});

/**
 * Inline text in one of the shared tones, sizes and weights. Leaving a
 * variant out inherits it from the parent. Renders a `span`; pass `asChild`
 * with a single child element (`p`, `label`, `div`) to style that element
 * instead. Overrides go in `className` here, not on the child, because the
 * child's own classes are appended without resolving conflicts.
 */
export const Text = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<"span"> &
    VariantProps<typeof textVariants> & { asChild?: boolean }
>(function Text(
  { asChild = false, className, tone, size, weight, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "span";
  return (
    <Comp
      className={cn(textVariants({ tone, size, weight }), className)}
      ref={ref}
      {...props}
    />
  );
});
